/**
 * Lab Aggregate Members multi ui-select — pick members with `_id` + level
 * via `POST /v1/member/searchHierarchyPage` (string prefix, `noPlayer` = TM/DM/SM/M).
 */
import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { Field, Input, Spinner } from '@w7/ui'
import type { AggregateMemberListItem } from '@/api/cmApi'
import {
  searchHierarchyPageForPicker,
  toMemberListItem,
} from '@/api/map/aggregateMembers'

const DEBOUNCE_MS = 100
const SUGGEST_LIMIT = 20

export type AggregateMemberPickerProps = {
  value: AggregateMemberListItem[]
  onChange: (members: AggregateMemberListItem[]) => void
  disabled?: boolean
}

export function AggregateMemberPicker({
  value,
  onChange,
  disabled,
}: AggregateMemberPickerProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const [draft, setDraft] = useState('')
  const [debounced, setDebounced] = useState('')
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(draft), DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [draft])

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      const root = rootRef.current
      if (root && !root.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [open])

  const membersQ = useQuery({
    queryKey: ['cm', 'aggregate-members', 'picker', debounced],
    queryFn: () => searchHierarchyPageForPicker(debounced),
    enabled: open && !disabled && debounced.trim().length > 0,
    staleTime: 10_000,
    retry: false,
  })

  const selectedIds = new Set(value.map((m) => m._id.toLowerCase()))
  const suggestions = (membersQ.data ?? [])
    .filter((r) => !selectedIds.has(r._id.toLowerCase()))
    .slice(0, SUGGEST_LIMIT)

  const pick = (row: (typeof suggestions)[number]) => {
    if (selectedIds.has(row._id.toLowerCase())) return
    onChange([...value, toMemberListItem(row)])
    setDraft('')
    setOpen(false)
  }

  const remove = (id: string) => {
    onChange(value.filter((m) => m._id !== id))
  }

  return (
    <div className="cm-group-member-picker cm-aggregate-member-picker" ref={rootRef}>
      <Field label="Select Users" required>
        {({ id, describedBy }) => (
          <div className="cm-cheater-player-wrap">
            {value.length ? (
              <ul className="cm-group-member-chips" aria-label="Selected users">
                {value.map((m) => (
                  <li key={m._id}>
                    <button
                      type="button"
                      className="cm-group-member-chip"
                      disabled={disabled}
                      onClick={() => remove(m._id)}
                      aria-label={`Remove ${m.name}`}
                    >
                      {m.name} ×
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            <Input
              id={id}
              aria-describedby={describedBy}
              placeholder="Enter or Select Member Name to Search"
              value={draft}
              disabled={disabled}
              aria-expanded={open}
              aria-controls="cm-aggregate-member-suggest"
              aria-autocomplete="list"
              aria-busy={membersQ.isFetching}
              onChange={(e) => {
                setDraft(e.target.value)
                setOpen(true)
              }}
              onFocus={() => setOpen(true)}
            />
            {open && !disabled && debounced.trim() ? (
              <ul
                id="cm-aggregate-member-suggest"
                className="cm-cheater-player-suggest"
                role="listbox"
              >
                {membersQ.isFetching && suggestions.length === 0 ? (
                  <li className="cm-cheater-player-empty" role="presentation">
                    <Spinner size={14} /> Searching…
                  </li>
                ) : null}
                {suggestions.length === 0 && !membersQ.isFetching ? (
                  <li className="cm-cheater-player-empty">No users found</li>
                ) : (
                  suggestions.map((row) => (
                    <li key={row._id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={false}
                        className="cm-cheater-player-option"
                        onMouseDown={(e) => {
                          e.preventDefault()
                          pick(row)
                        }}
                      >
                        {row.username}
                      </button>
                    </li>
                  ))
                )}
              </ul>
            ) : null}
          </div>
        )}
      </Field>
    </div>
  )
}
